import React, { useState, useRef, useEffect, useCallback } from 'react'
import {
  IconButton,
  Box,
  Paper,
  styled,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material'
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import { DocumentDrawer, SelectedDocument, TDocument } from '../DocumentDrawer'
import ImageDrawer from '@/routes/Quizzes/ImageDrawer'
import VideoDrawer from '@/routes/Quizzes/VideoDrawer'
import { useTranslation } from 'react-i18next'
import { getAllDocumentsNewFlow } from '@/store/knowledge/actions'
import { useDispatch } from 'react-redux'
import {
  createDocumentLinkIcon,
  createImageIcon,
  createVideoIcon,
  getDocumentLinkIconHtml,
  getImageIconHtml,
  getVideoIconHtml,
} from './badgeIcons'
import {
  extractMarkdownFromSelection,
  selectionContainsBadges,
  getHtmlFromClonedContents,
} from './clipboardUtils'

interface ContenteditableProps {
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  loading?: boolean
  viewMode?: boolean
}

// Custom styled contenteditable div
const EditableDiv = styled('div')({
  outline: 'none',
  minHeight: '100px',
  whiteSpace: 'pre-wrap',
  width: '100%',
  '& .linkedText': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    margin: '0 2px',
    backgroundColor: '#D3E8FF',
    color: '#001DE0',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #001DE0',
    userSelect: 'none',
    WebkitUserDrag: 'element',
  },
  '& .linkedText:hover': {
    backgroundColor: '#B8D4FF',
  },
  '& .embeddedImage': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    margin: '0 2px',
    backgroundColor: '#D3FFED',
    color: '#00864D',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #00864D',
    userSelect: 'none',
    WebkitUserDrag: 'element',
  },
  '& .embeddedImage:hover': {
    backgroundColor: '#B8FFDE',
  },
  '& .embeddedVideo': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    margin: '0 2px',
    backgroundColor: '#FEE5F4',
    color: '#FF44A6',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid #FF44A6',
    userSelect: 'none',
    WebkitUserDrag: 'element',
  },
  '& .embeddedVideo:hover': {
    backgroundColor: '#FFD0E8',
  },
  '& p': {
    fontSize: '14px',
  },
})

const linkRegex = /\[([^\]]+)\]\(document_id:\/\/([^)]+)\)/g
const imageRegex = /!\[([^\]]*)\]\(image:\/\/([^)]+)\)/g
const videoRegex = /!video\[([^\]]*)\]\(video:\/\/([^)]+)\)/g

export const Contenteditable: React.FC<ContenteditableProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter text here...',
  className = '',
  style = {},
  loading = false,
  viewMode = false,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedText, setSelectedText] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [imageDrawerOpen, setImageDrawerOpen] = useState(false)
  const [videoDrawerOpen, setVideoDrawerOpen] = useState(false)
  const [imageDrawerInitialData, setImageDrawerInitialData] = useState<{
    id?: string
    ids?: string[]
    altText?: string
  }>()
  const [videoDrawerInitialData, setVideoDrawerInitialData] = useState<{
    id?: string
    ids?: string[]
    title?: string
  }>()
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<
    SelectedDocument[]
  >([])

  // TODO: start - state for managing documents
  const [documents, setDocuments] = useState<TDocument[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [yearOptions, setYearOptions] = useState<string[]>([])
  const [moduleOptions, setModuleOptions] = useState<string[]>([])
  const [topicOptions, setTopicOptions] = useState<string[]>([])
  // TODO: end - state for managing documents

  const [savedRange, setSavedRange] = useState<Range | null>(null)

  const [selectedRangeElement, setSelectedRangeElement] = useState<{
    element: HTMLElement | null
    position: { top: number; left: number } | null
  }>({ element: null, position: null })

  const editableDivRef = useRef<HTMLDivElement>(null)
  const draggedElementRef = useRef<HTMLElement | null>(null)

  const fetchDocuments = async () => {
    // setLoading(true)
    try {
      const { documents: documentsRes }: any = await dispatch(
        getAllDocumentsNewFlow({
          page: 1,
          page_size: 10000,
        }),
      )
      if (documentsRes) {
        setDocuments(documentsRes)
        // TODO: pass as props to DocumentDrawer
        // setFilteredDocuments(documentsRes)

        const uniqueYears = new Set(
          documentsRes.map((doc: TDocument) => doc.knowledge_group_1_name),
        )
        setYearOptions(Array.from(uniqueYears).filter(Boolean) as string[])

        const uniqueModules = new Set(
          documentsRes.map((doc: TDocument) => doc.knowledge_group_2_name),
        )
        setModuleOptions(Array.from(uniqueModules).filter(Boolean) as string[])

        const uniqueTopics = new Set(
          documentsRes.map((doc: TDocument) => doc?.knowledge_group_3_name),
        )

        setTopicOptions(Array.from(uniqueTopics).filter(Boolean) as string[])
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
    } finally {
      setIsLoaded(true)
      // setLoading(false)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [])

  const handleInput = () => {
    if (editableDivRef.current) {
      const html = editableDivRef.current.innerHTML

      const updatedRawContent = convertHtmlToFormat(html)

      if (onChange) {
        onChange(updatedRawContent)
      }
    }
  }

  const convertHtmlToFormat = (html: string) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    // Find all linked text spans and replace them with the markdown format
    const linkedSpans = tempDiv.querySelectorAll('.linkedText')
    linkedSpans.forEach(span => {
      const documentId = span.getAttribute('data-document-id')
      // Remove SVG icon text from innerText (icon is rendered separately)
      let innerText = span.textContent || ''

      // Create a text node with the markdown format
      const formattedText = document.createTextNode(
        `[${innerText}](${documentId})`,
      )

      // Replace the span with the formatted text
      span.parentNode?.replaceChild(formattedText, span)
    })

    // Find all embedded image spans and replace them with markdown format
    const imageSpans = tempDiv.querySelectorAll('.embeddedImage')
    imageSpans.forEach(span => {
      const imageIdsString =
        span.getAttribute('data-image-id') ||
        span.getAttribute('data-image-src') ||
        ''
      const imageIds = imageIdsString.split(';').filter(Boolean)
      const altText = span.textContent?.replace('🖼️ ', '') || ''

      // Format: image://id1;image://id2 (same pattern as Reference)
      const idsFormatted = imageIds.map(id => `image://${id}`).join(';')
      const formattedText = document.createTextNode(
        `![${altText}](${idsFormatted})`,
      )

      span.parentNode?.replaceChild(formattedText, span)
    })

    // Find all embedded video spans and replace them with markdown format
    const videoSpans = tempDiv.querySelectorAll('.embeddedVideo')
    videoSpans.forEach(span => {
      const videoId =
        span.getAttribute('data-video-id') ||
        span.getAttribute('data-video-src')
      const title = span.textContent?.replace('🎥 ', '') || ''

      const formattedText = document.createTextNode(
        `!video[${title}](video://${videoId})`,
      )

      span.parentNode?.replaceChild(formattedText, span)
    })

    // Convert the HTML to plain text while preserving line breaks
    let content = ''
    const childNodes = Array.from(tempDiv.childNodes)

    childNodes.forEach((node, index) => {
      // For paragraphs and divs, add newlines between them
      if (node.nodeName === 'P' || node.nodeName === 'DIV') {
        content += node.textContent
        // Add newline after paragraphs except for the last one
        if (index < childNodes.length - 1) {
          content += '\n'
        }
      }
      // Handle BR tags
      else if (node.nodeName === 'BR') {
        content += '\n'
      }
      // Handle text nodes and other elements
      else {
        content += node.textContent
      }
    })

    return content
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault() // Prevent default behavior

      // Insert a <br> element at the cursor position
      const selection = window.getSelection()
      const range = selection?.getRangeAt(0)

      if (range && editableDivRef.current) {
        // Create a <br> element
        const br = document.createElement('br')

        // Insert the <br> element at the cursor position
        range.deleteContents()
        range.insertNode(br)

        // Move caret after the <br>
        range.setStartAfter(br)
        range.setEndAfter(br)
        range.collapse(true)

        // Update selection
        selection?.removeAllRanges()
        selection?.addRange(range)

        // Trigger onChange
        handleInput()
      }
    }
    if (
      ['i', 'b', 'u'].includes(event.key) &&
      (event.ctrlKey || event.metaKey)
    ) {
      event.preventDefault()
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    // Show caret at drop position for visual feedback
    let range: Range | null = null
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(event.clientX, event.clientY)
    } else if ((document as any).caretPositionFromPoint) {
      const position = (document as any).caretPositionFromPoint(
        event.clientX,
        event.clientY,
      )
      if (position) {
        range = document.createRange()
        range.setStart(position.offsetNode, position.offset)
        range.collapse(true)
      }
    }

    if (range) {
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()

    if (!editableDivRef.current || !onChange) return

    const droppedMarkdown = event.dataTransfer.getData('text/plain')
    if (!droppedMarkdown) return

    // Step 1: Remove the original dragged element
    if (draggedElementRef.current && draggedElementRef.current.parentNode) {
      draggedElementRef.current.parentNode.removeChild(
        draggedElementRef.current,
      )
      draggedElementRef.current = null
    }

    // Step 2: Get drop position and insert markdown text temporarily
    let range: Range | null = null
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(event.clientX, event.clientY)
    } else if ((document as any).caretPositionFromPoint) {
      const position = (document as any).caretPositionFromPoint(
        event.clientX,
        event.clientY,
      )
      if (position) {
        range = document.createRange()
        range.setStart(position.offsetNode, position.offset)
        range.collapse(true)
      }
    }

    if (range) {
      const textNode = document.createTextNode(droppedMarkdown + ' ')
      range.insertNode(textNode)
    }

    // Step 3: Convert current innerHTML to markdown (includes our dropped text)
    const newMarkdownContent = convertHtmlToFormat(
      editableDivRef.current.innerHTML,
    )

    // Step 4: Force re-render innerHTML with proper badges (same as useEffect does)
    editableDivRef.current.innerHTML = convertFormatToHtml(newMarkdownContent)

    // Step 5: Notify parent with new markdown content
    onChange(newMarkdownContent)

    // Step 6: Re-attach listeners for badges
    attachLinkListeners()
  }

  const handleEditLink = useCallback((element: HTMLElement) => {
    if (!element) return

    // Clean state from other drawers first
    setImageDrawerOpen(false)
    setVideoDrawerOpen(false)
    setImageDrawerInitialData(undefined)
    setVideoDrawerInitialData(undefined)

    // Get the text content from the span
    const textContent = element.textContent || ''

    // Set the selected text and save a range for the span
    setSelectedText(textContent)

    // Create a range for the span
    const range = document.createRange()
    range.selectNode(element)
    setSavedRange(range)

    // Store reference to element for delete operation
    setSelectedRangeElement({
      element: element,
      position: null,
    })

    // Open the document drawer
    // Each badge now contains only one document ID (no longer using ';' separator)
    const documentId = element.getAttribute('data-document-id')
    if (documentId) {
      const url = documentId.replace('document_id://', '')
      const [id, query] = url.split('?')
      const params = new URLSearchParams(query || '')
      const fromPage = params.get('pageFrom')
      const toPage = params.get('pageTo')

      const document = {
        id,
        name: '',
        allPages: !fromPage && !toPage,
        fromPage: Number(fromPage) || 0,
        toPage: Number(toPage) || 0,
      }
      setSelectedDocumentIds([document])
    }

    setDrawerOpen(true)
  }, [])

  const handleLinkedTextClick = useCallback(
    (event: Event) => {
      handleEditLink(event.currentTarget as HTMLElement)
    },
    [handleEditLink],
  )

  const attachLinkListeners = useCallback(() => {
    // Selector for all badge types
    const badgeSelector = '.linkedText, .embeddedImage, .embeddedVideo'

    // First remove any existing listeners to prevent duplicates
    const existingBadges = document.querySelectorAll(badgeSelector)
    existingBadges.forEach(span => {
      // Remove old handlers
      const oldClickHandler = (span as any)._clickHandler
      const oldDragStartHandler = (span as any)._dragStartHandler
      const oldDragEndHandler = (span as any)._dragEndHandler
      if (oldClickHandler) span.removeEventListener('click', oldClickHandler)
      if (oldDragStartHandler)
        span.removeEventListener('dragstart', oldDragStartHandler)
      if (oldDragEndHandler)
        span.removeEventListener('dragend', oldDragEndHandler)
    })

    // Then add listeners to all badges
    if (editableDivRef.current) {
      const badges = editableDivRef.current.querySelectorAll(badgeSelector)
      badges.forEach(span => {
        // Click listener - open edit popup
        const clickHandler = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
          const badge = e.currentTarget as HTMLElement

          // Check badge type and open appropriate drawer
          const isLinkedText = badge.classList.contains('linkedText')
          const isEmbeddedImage = badge.classList.contains('embeddedImage')
          const isEmbeddedVideo = badge.classList.contains('embeddedVideo')

          if (isLinkedText) {
            handleEditLink(badge)
          } else if (isEmbeddedImage) {
            // Clean state from other drawers first
            setDrawerOpen(false)
            setVideoDrawerOpen(false)
            setSelectedDocumentIds([])
            setVideoDrawerInitialData(undefined)
            setSelectedText('')

            // Extract image data and open drawer
            const imageIdsString =
              badge.getAttribute('data-image-id') ||
              badge.getAttribute('data-image-src') ||
              ''
            const markdown = badge.getAttribute('data-markdown')

            // Extract label: try markdown first, then badge text content
            let altText = ''
            if (markdown) {
              const match = markdown.match(/!\[([^\]]*)\]/)
              if (match) altText = match[1]
            }
            // Fallback to badge text content (excluding icon)
            if (!altText) {
              altText = badge.textContent?.trim() || ''
            }

            let finalImageIds: string[] = []
            if (imageIdsString) {
              finalImageIds = imageIdsString.split(';').filter(Boolean)
            } else if (markdown) {
              const idMatch = markdown.match(/image:\/\/([^)]+)/)
              if (idMatch) {
                const idsString = idMatch[1]
                finalImageIds = idsString
                  .split(';')
                  .map(id => id.replace('image://', '').trim())
                  .filter(Boolean)
                finalImageIds = finalImageIds.filter(
                  id => !id.startsWith('blob:'),
                )
              }
            }
            const range = document.createRange()
            range.selectNode(badge)
            setSavedRange(range)
            setSelectedRangeElement({
              element: badge,
              position: null,
            })
            setImageDrawerInitialData({
              id: finalImageIds[0],
              ids: finalImageIds.length > 1 ? finalImageIds : undefined,
              altText: altText,
            })
            setImageDrawerOpen(true)
          } else if (isEmbeddedVideo) {
            // Clean state from other drawers first
            setDrawerOpen(false)
            setImageDrawerOpen(false)
            setSelectedDocumentIds([])
            setImageDrawerInitialData(undefined)
            setSelectedText('')

            // Extract video data and open drawer
            const videoId =
              badge.getAttribute('data-video-id') ||
              badge.getAttribute('data-video-src')
            const markdown = badge.getAttribute('data-markdown')

            // Extract label: try markdown first, then badge text content
            let title = ''
            if (markdown) {
              const match = markdown.match(/!video\[([^\]]*)\]/)
              if (match) title = match[1]
            }
            // Fallback to badge text content (excluding icon)
            if (!title) {
              title = badge.textContent?.trim() || ''
            }

            let finalVideoId = videoId || ''
            if (!finalVideoId && markdown) {
              const idMatch = markdown.match(/video:\/\/([^)]+)/)
              if (idMatch) {
                finalVideoId = idMatch[1]
                if (finalVideoId.startsWith('blob:')) {
                  finalVideoId = ''
                }
              }
            }
            const range = document.createRange()
            range.selectNode(badge)
            setSavedRange(range)
            setSelectedRangeElement({
              element: badge,
              position: null,
            })
            setVideoDrawerInitialData({
              id: finalVideoId,
              ids: finalVideoId ? [finalVideoId] : [],
              title: title,
            })
            setVideoDrawerOpen(true)
          }
        }

        // Make badge behave like a button - not editable, fully draggable
        const badgeElement = span as HTMLElement
        badgeElement.setAttribute('contenteditable', 'false')
        badgeElement.setAttribute('draggable', 'true')
        badgeElement.style.cursor = 'pointer'

        // Track if we're dragging to distinguish from click
        let isDragging = false

        const dragStartHandler = (e: Event) => {
          const dragEvent = e as DragEvent
          const badge = e.currentTarget as HTMLElement
          if (!badge || !dragEvent.dataTransfer) return

          isDragging = true

          // Same logic as Move button in toolbar
          const markdown = badge.getAttribute('data-markdown')
          if (markdown) {
            dragEvent.dataTransfer.setData('text/plain', markdown)
            dragEvent.dataTransfer.effectAllowed = 'move'
            draggedElementRef.current = badge
            badge.style.opacity = '0.5'
          }
        }

        const dragEndHandler = (e: Event) => {
          const badge = e.currentTarget as HTMLElement
          if (!badge) return

          badge.style.opacity = '1'
          isDragging = false
        }

        // Click handler wrapper - only fire if not dragging
        const wrappedClickHandler = (e: Event) => {
          if (isDragging) {
            isDragging = false
            return
          }
          clickHandler(e)
        }

        // Store handlers for cleanup
        ;(span as any)._clickHandler = wrappedClickHandler
        ;(span as any)._dragStartHandler = dragStartHandler
        ;(span as any)._dragEndHandler = dragEndHandler

        // Add event listeners
        span.addEventListener('click', wrappedClickHandler)
        span.addEventListener('dragstart', dragStartHandler)
        span.addEventListener('dragend', dragEndHandler)
      })
    }
  }, [handleEditLink, draggedElementRef])

  const handleIconClick = () => {
    // Clean state from other drawers first - including selectedText from previous operations
    setImageDrawerOpen(false)
    setVideoDrawerOpen(false)
    setImageDrawerInitialData(undefined)
    setVideoDrawerInitialData(undefined)
    setSelectedRangeElement({ element: null, position: null })
    setSelectedText('') // Clean selectedText before getting new selection

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      // No cursor/selection - create range at end of content
      if (editableDivRef.current) {
        const range = document.createRange()
        range.selectNodeContents(editableDivRef.current)
        range.collapse(false) // Collapse to end
        setSavedRange(range)
      } else {
        setSavedRange(null)
      }
      setDrawerOpen(true)
      return
    }
    const range = selection.getRangeAt(0)

    // Collapse range to end of selection (so badge inserts AFTER selected text)
    const collapsedRange = range.cloneRange()
    collapsedRange.collapse(false) // false = collapse to end
    setSavedRange(collapsedRange)

    const text = range.toString()
    setSelectedText(text) // Set new selected text
    setDrawerOpen(true)
  }

  // Handler for Image icon when text is selected
  const handleImageIconClick = () => {
    // Clean state from other drawers first - including selectedText from previous operations
    setDrawerOpen(false)
    setVideoDrawerOpen(false)
    setSelectedDocumentIds([])
    setVideoDrawerInitialData(undefined)
    setSelectedRangeElement({ element: null, position: null })
    setSelectedText('') // Clean selectedText before getting new selection

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      // No cursor/selection - create range at end of content
      if (editableDivRef.current) {
        const range = document.createRange()
        range.selectNodeContents(editableDivRef.current)
        range.collapse(false) // Collapse to end
        setSavedRange(range)
      } else {
        setSavedRange(null)
      }
      setImageDrawerInitialData(undefined)
      setImageDrawerOpen(true)
      return
    }
    const range = selection.getRangeAt(0)

    // Collapse range to end of selection (so badge inserts AFTER selected text)
    const collapsedRange = range.cloneRange()
    collapsedRange.collapse(false)
    setSavedRange(collapsedRange)

    const text = range.toString()
    setSelectedText(text) // Set new selected text
    setImageDrawerInitialData({ altText: text })
    setImageDrawerOpen(true)
  }

  // Handler for Video icon when text is selected
  const handleVideoIconClick = () => {
    // Clean state from other drawers first - including selectedText from previous operations
    setDrawerOpen(false)
    setImageDrawerOpen(false)
    setSelectedDocumentIds([])
    setImageDrawerInitialData(undefined)
    setSelectedRangeElement({ element: null, position: null })
    setSelectedText('') // Clean selectedText before getting new selection

    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      // No cursor/selection - create range at end of content
      if (editableDivRef.current) {
        const range = document.createRange()
        range.selectNodeContents(editableDivRef.current)
        range.collapse(false) // Collapse to end
        setSavedRange(range)
      } else {
        setSavedRange(null)
      }
      setVideoDrawerInitialData(undefined)
      setVideoDrawerOpen(true)
      return
    }
    const range = selection.getRangeAt(0)

    // Collapse range to end of selection (so badge inserts AFTER selected text)
    const collapsedRange = range.cloneRange()
    collapsedRange.collapse(false)
    setSavedRange(collapsedRange)

    const text = range.toString()
    setSelectedText(text) // Set new selected text
    setVideoDrawerInitialData({ title: text })
    setVideoDrawerOpen(true)
  }

  // Clean all drawer-related state
  const cleanDrawerState = () => {
    setSelectedText('')
    setSavedRange(null)
    setSelectedRangeElement({ element: null, position: null })
    setSelectedDocumentIds([])
    setImageDrawerInitialData(undefined)
    setVideoDrawerInitialData(undefined)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    cleanDrawerState()
  }

  const handleDocumentSelect = (
    documents: SelectedDocument[],
    labelText?: string,
  ) => {
    if (!editableDivRef.current || !onChange) {
      return
    }

    if (documents.length === 0) {
      return
    }

    // Check if we're editing an existing document badge
    let remainingDocuments = documents
    if (selectedRangeElement.element) {
      const element = selectedRangeElement.element as HTMLElement
      if (element.classList.contains('linkedText')) {
        // Update existing document badge with first selected document
        const doc = documents[0]
        const documentLink = `document_id://${doc.id}${
          doc.allPages ? '' : `?pageFrom=${doc.fromPage}&pageTo=${doc.toPage}`
        }`

        // Priority: labelText (from Name field) > selectedText (from badge) > document name
        const customLabel = labelText?.trim()
        const existingText = element.textContent?.trim() || ''
        const baseLabel = customLabel || existingText || doc.name

        // Add page range to display text if not all pages
        let displayText = baseLabel
        if (!doc.allPages && doc.fromPage && doc.toPage) {
          displayText = `${baseLabel} (${doc.fromPage}-${doc.toPage})`
        }

        const markdown = `[${displayText}](${documentLink})`

        // Update existing badge
        element.setAttribute('data-document-id', documentLink)
        element.setAttribute('data-markdown', markdown)

        // Update badge content (remove old content and add new)
        element.innerHTML = ''
        const iconSvg = createDocumentLinkIcon()
        element.appendChild(iconSvg)
        element.appendChild(document.createTextNode(displayText))

        // If there are more documents, create badges for them (insert after the updated badge)
        remainingDocuments = documents.slice(1)

        // If no remaining documents, we're done - just update and return
        if (remainingDocuments.length === 0) {
          // Update the content
          const html = editableDivRef.current.innerHTML
          const formatted = convertHtmlToFormat(html)
          onChange(formatted)

          // Use setTimeout to ensure DOM is updated before attaching listeners
          setTimeout(() => {
            attachLinkListeners()
          }, 0)
          setDrawerOpen(false)
          cleanDrawerState()
          return
        }

        // There are remaining documents - we'll create badges for them below
        // Create a range right after the updated badge for inserting new badges
        const range = document.createRange()
        range.setStartAfter(element)
        range.collapse(true)
        setSavedRange(range)
      }
    }

    // Create badges for remaining documents (either all documents if new, or remaining after edit)
    // If no savedRange, create one at end of content
    if (!savedRange) {
      if (!editableDivRef.current) {
        return
      }
      const range = document.createRange()
      range.selectNodeContents(editableDivRef.current)
      range.collapse(false) // Collapse to end
      setSavedRange(range)
    }

    // Priority for display name:
    // - When editing: Use document name for new badges (each document should have its own name)
    // - When creating new: Use selectedText (if user selected text) > labelText > document name
    const isEditing =
      selectedRangeElement.element?.classList.contains('linkedText')
    const baseLabel = isEditing
      ? '' // When editing, don't use baseLabel - each document should use its own name
      : selectedText || labelText || '' // When creating new, use selectedText first

    // Create HTML for remaining document badges
    const documentSpans = remainingDocuments.map((doc, index) => {
      const documentLink = `document_id://${doc.id}${
        doc.allPages ? '' : `?pageFrom=${doc.fromPage}&pageTo=${doc.toPage}`
      }`

      // When editing, always use document's own name; when creating new, prefer baseLabel if available
      let displayText = isEditing
        ? doc.name // When editing, use document's own name
        : baseLabel || doc.name // When creating new, use baseLabel if available, otherwise doc.name

      // Add page range to display text if not all pages
      if (!doc.allPages && doc.fromPage && doc.toPage) {
        displayText = `${displayText} (${doc.fromPage}-${doc.toPage})`
      }

      const markdown = `[${displayText}](${documentLink})`

      const span = document.createElement('span')
      span.className = 'linkedText'
      span.setAttribute('data-document-id', documentLink)
      span.setAttribute('data-markdown', markdown)
      span.setAttribute('draggable', 'true')
      span.setAttribute('contenteditable', 'false')

      // Create SVG icon for document using reusable helper
      const iconSvg = createDocumentLinkIcon()
      span.appendChild(iconSvg)
      span.appendChild(document.createTextNode(displayText))

      return span
    })

    if (editableDivRef.current.contains(savedRange.commonAncestorContainer)) {
      // Restore selection and insert at cursor position
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(savedRange)
      }

      // Insert each document span at cursor position
      documentSpans.forEach((span, index) => {
        savedRange.insertNode(span)
        // Move cursor after inserted span
        savedRange.setStartAfter(span)
        savedRange.collapse(true)
        // Add space between documents
        if (index < documentSpans.length - 1) {
          const space = document.createTextNode(' ')
          savedRange.insertNode(space)
          savedRange.setStartAfter(space)
          savedRange.collapse(true)
        }
      })
    } else {
      // Fallback: append at end of content
      documentSpans.forEach((span, index) => {
        if (index > 0) {
          editableDivRef.current!.appendChild(document.createTextNode(' '))
        }
        editableDivRef.current!.appendChild(span)
      })
    }

    // Update content
    const html = editableDivRef.current.innerHTML
    const formatted = convertHtmlToFormat(html)
    onChange(formatted)

    // Use setTimeout to ensure DOM is updated before attaching listeners
    setTimeout(() => {
      attachLinkListeners()
    }, 0)
    cleanDrawerState()
  }

  const handleImageSave = async (imageData: {
    file?: File
    id?: string
    ids?: string[]
    altText?: string
    fileNames?: string[] // Array of file names for multiple uploads
  }) => {
    if (!editableDivRef.current || !onChange) return

    // Handle multiple IDs from drawer (new uploads)
    let newImageIds: string[] = []
    if (imageData.ids && imageData.ids.length > 0) {
      newImageIds = imageData.ids
    } else if (imageData.id) {
      newImageIds = [imageData.id]
    }

    // Check if we're editing an existing image - MERGE IDs
    if (selectedRangeElement.element) {
      const element = selectedRangeElement.element as HTMLElement
      if (element.classList.contains('embeddedImage')) {
        // Use new IDs directly (replace existing)
        const combinedIds = newImageIds

        if (combinedIds.length === 0) {
          setImageDrawerOpen(false)
          cleanDrawerState()
          return
        }

        // Generate updated markdown
        // Use custom altText from drawer if provided, otherwise use existing or default
        const customAltText = imageData.altText?.trim()
        const existingAltText =
          element.textContent
            ?.replace(/🖼️\s*/, '')
            .replace(/Image\s*\+\d+/, 'Image')
            .trim() || 'Image'
        const baseAltText = customAltText || existingAltText

        const displayText =
          combinedIds.length > 1
            ? `${baseAltText} +${combinedIds.length - 1}`
            : baseAltText
        const imageIdsString = combinedIds.map(id => `image://${id}`).join(';')
        const markdown = `![${displayText}](${imageIdsString})`

        // Update existing image badge
        element.setAttribute('data-image-id', combinedIds.join(';'))
        element.setAttribute('data-markdown', markdown)
        element.innerHTML = `${getImageIconHtml()}${displayText}`

        // Update the content
        const html = editableDivRef.current.innerHTML
        const formatted = convertHtmlToFormat(html)
        onChange(formatted)
        // Use setTimeout to ensure DOM is updated before attaching listeners
        setTimeout(() => {
          attachLinkListeners()
        }, 0)
        setImageDrawerOpen(false)
        cleanDrawerState()
        return
      }
    }

    // New image - create separate badges for each image
    if (newImageIds.length === 0) {
      return
    }

    // Create separate markdown for each image (easier to manage and edit)
    // Use file names from fileNames array if available, otherwise use custom altText or file name
    const customAltText = imageData.altText?.trim()
    const fileNames = imageData.fileNames || []

    // Create HTML for all image badges
    const imageSpans = newImageIds.map((id, index) => {
      // Priority: 1. fileNames[index] (actual file name), 2. customAltText (user input), 3. imageData.file?.name (single file), 4. fallback
      let displayText = ''
      if (fileNames[index]) {
        // Use actual file name from upload
        displayText = fileNames[index]
      } else if (customAltText && newImageIds.length === 1) {
        // Single image with custom name
        displayText = customAltText
      } else if (imageData.file?.name && newImageIds.length === 1) {
        // Single file upload
        displayText = imageData.file.name
      } else if (customAltText && newImageIds.length > 1) {
        // Multiple images with custom base name
        displayText = `${customAltText} ${index + 1}`
      } else {
        // Fallback - should not happen with proper file name passing
        displayText = `Image ${index + 1}`
      }
      const markdown = `![${displayText}](image://${id})`

      const span = document.createElement('span')
      span.className = 'embeddedImage'
      span.setAttribute('data-image-id', id)
      span.setAttribute('data-markdown', markdown)
      span.setAttribute('draggable', 'true')
      span.setAttribute('contenteditable', 'false')

      // Create image icon
      // Create SVG icon for image using reusable helper
      const iconSvg = createImageIcon()
      span.appendChild(iconSvg)
      span.appendChild(document.createTextNode(displayText))

      return span
    })

    // Insert at cursor position if savedRange exists, otherwise append at end
    if (
      savedRange &&
      editableDivRef.current &&
      editableDivRef.current.contains(savedRange.commonAncestorContainer)
    ) {
      // Restore selection and insert at cursor position
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(savedRange)
      }

      // Insert each image span at cursor position
      imageSpans.forEach((span, index) => {
        savedRange.insertNode(span)
        // Move cursor after inserted span
        savedRange.setStartAfter(span)
        savedRange.collapse(true)
        // Add space between images
        if (index < imageSpans.length - 1) {
          const space = document.createTextNode(' ')
          savedRange.insertNode(space)
          savedRange.setStartAfter(space)
          savedRange.collapse(true)
        }
      })
    } else {
      // Fallback: append at end of content (when no cursor or savedRange is null)
      if (editableDivRef.current) {
        imageSpans.forEach((span, index) => {
          if (index > 0) {
            editableDivRef.current!.appendChild(document.createTextNode(' '))
          }
          editableDivRef.current!.appendChild(span)
        })
      }
    }

    // Update content
    const html = editableDivRef.current.innerHTML
    const formatted = convertHtmlToFormat(html)
    onChange(formatted)

    // Use setTimeout to ensure DOM is updated before attaching listeners
    setTimeout(() => {
      attachLinkListeners()
    }, 0)
    setImageDrawerOpen(false)
    cleanDrawerState()
  }

  const handleRemoveImageId = (idToRemove: string) => {
    if (!editableDivRef.current || !onChange || !selectedRangeElement.element)
      return

    const element = selectedRangeElement.element as HTMLElement
    if (!element.classList.contains('embeddedImage')) return

    // Get current IDs from badge
    const currentIdsString = element.getAttribute('data-image-id') || ''
    const currentIds = currentIdsString.split(';').filter(Boolean)

    // Remove the ID
    const newIds = currentIds.filter(id => id !== idToRemove)

    if (newIds.length === 0) {
      // If no IDs left, remove the badge entirely
      element.remove()
      const html = editableDivRef.current.innerHTML
      const formatted = convertHtmlToFormat(html)
      onChange(formatted)
      setTimeout(() => {
        attachLinkListeners()
      }, 0)
      setImageDrawerOpen(false)
      cleanDrawerState()
      return
    }

    // Update badge with remaining IDs
    const altText = element.textContent?.replace(/🖼️\s*/, '') || 'Image'
    const displayText =
      newIds.length > 1
        ? `${altText.split(' +')[0]} +${newIds.length - 1}`
        : altText.split(' +')[0]
    const imageIdsString = newIds.map(id => `image://${id}`).join(';')
    const markdown = `![${displayText}](${imageIdsString})`

    element.setAttribute('data-image-id', newIds.join(';'))
    element.setAttribute('data-markdown', markdown)
    element.innerHTML = `${getImageIconHtml()}${displayText}`

    // Update the content
    const html = editableDivRef.current.innerHTML
    const formatted = convertHtmlToFormat(html)
    onChange(formatted)
    setTimeout(() => {
      attachLinkListeners()
    }, 0)

    // Update initialData to reflect the change
    setImageDrawerInitialData({
      id: newIds[0],
      ids: newIds.length > 1 ? newIds : undefined,
      altText: displayText,
    })
  }

  const handleVideoSave = async (videoData: {
    file?: File
    id?: string
    ids?: string[]
    title?: string
    fileNames?: string[] // Array of file names for multiple uploads
    urlNames?: string[] // Array of URL names for multiple URLs
  }) => {
    if (!editableDivRef.current || !onChange) return

    // Handle multiple IDs from drawer (new uploads)
    let newVideoIds: string[] = []
    if (videoData.ids && videoData.ids.length > 0) {
      newVideoIds = videoData.ids
    } else if (videoData.id) {
      newVideoIds = [videoData.id]
    }

    // Check if we're editing an existing video - MERGE IDs
    if (selectedRangeElement.element) {
      const element = selectedRangeElement.element as HTMLElement
      if (element.classList.contains('embeddedVideo')) {
        // Use new IDs directly (replace existing)
        const combinedIds = newVideoIds

        if (combinedIds.length === 0) {
          // If no IDs (all removed), remove the badge
          element.remove()
          const html = editableDivRef.current.innerHTML
          const formatted = convertHtmlToFormat(html)
          onChange(formatted)
          setTimeout(() => {
            attachLinkListeners()
          }, 0)
          setVideoDrawerOpen(false)
          cleanDrawerState()
          return
        }

        // Helper function to truncate URL if too long
        const truncateUrl = (url: string, maxLength: number = 50): string => {
          if (url.length <= maxLength) return url
          return url.substring(0, maxLength - 3) + '...'
        }

        // Generate updated markdown
        // Use custom title from drawer if provided, otherwise use existing text (which should be file/URL name)
        const customTitle = videoData.title?.trim()
        // Extract existing text, removing icon and "+N" suffix if present
        const existingText =
          element.textContent
            ?.replace(/🎥\s*/, '')
            .replace(/\s*\+\d+$/, '') // Remove "+N" suffix
            .trim() || ''
        // Use customTitle if provided, otherwise keep existing text (which should be file/URL name)
        // If existingText looks like a URL, truncate it
        const baseTitle = customTitle || existingText || 'Video'
        const displayTitle = baseTitle.startsWith('http')
          ? truncateUrl(baseTitle)
          : baseTitle

        const displayText =
          combinedIds.length > 1
            ? `${displayTitle} +${combinedIds.length - 1}`
            : displayTitle
        const videoIdsString = combinedIds.map(id => `video://${id}`).join(';')
        const markdown = `!video[${displayText}](${videoIdsString})`

        // Update existing video badge
        element.setAttribute('data-video-id', combinedIds.join(';'))
        element.setAttribute('data-markdown', markdown)
        element.innerHTML = `${getVideoIconHtml()}${displayText}`

        // Update the content
        const html = editableDivRef.current.innerHTML
        const formatted = convertHtmlToFormat(html)
        onChange(formatted)
        // Use setTimeout to ensure DOM is updated before attaching listeners
        setTimeout(() => {
          attachLinkListeners()
        }, 0)
        setVideoDrawerOpen(false)
        cleanDrawerState()
        return
      }
    }

    // New video - create separate badges for each video
    if (newVideoIds.length === 0) {
      setVideoDrawerOpen(false)
      cleanDrawerState()
      return
    }

    // Create separate markdown for each video (easier to manage and edit)
    // Use file names from fileNames array or urlNames array if available, otherwise use custom title or file name
    const customTitle = videoData.title?.trim()
    const fileNames = videoData.fileNames || []
    const urlNames = videoData.urlNames || []

    // Helper function to truncate URL if too long
    const truncateUrl = (url: string, maxLength: number = 50): string => {
      if (url.length <= maxLength) return url
      return url.substring(0, maxLength - 3) + '...'
    }

    // Create HTML for all video badges
    const videoSpans = newVideoIds.map((id, index) => {
      // Priority: 1. fileNames[index] or urlNames[index] (actual name), 2. customTitle (user input), 3. videoData.file?.name (single file), 4. fallback
      let displayText = ''
      if (fileNames[index]) {
        // Use actual file name from upload
        displayText = fileNames[index]
      } else if (urlNames[index]) {
        // Use URL name - truncate if too long
        displayText = truncateUrl(urlNames[index])
      } else if (customTitle && newVideoIds.length === 1) {
        // Single video with custom name
        displayText = customTitle
      } else if (videoData.file?.name && newVideoIds.length === 1) {
        // Single file upload
        displayText = videoData.file.name
      } else if (customTitle && newVideoIds.length > 1) {
        // Multiple videos with custom base name
        displayText = `${customTitle} ${index + 1}`
      } else {
        // Fallback - should not happen with proper file/URL name passing
        displayText = `Video ${index + 1}`
      }
      const markdown = `!video[${displayText}](video://${id})`

      const span = document.createElement('span')
      span.className = 'embeddedVideo'
      span.setAttribute('data-video-id', id)
      span.setAttribute('data-markdown', markdown)
      span.setAttribute('draggable', 'true')
      span.setAttribute('contenteditable', 'false')

      // Create video icon
      // Create SVG icon for video using reusable helper
      const iconSvg = createVideoIcon()
      span.appendChild(iconSvg)
      span.appendChild(document.createTextNode(displayText))

      return span
    })

    // Insert at cursor position if savedRange exists, otherwise append at end
    if (
      savedRange &&
      editableDivRef.current &&
      editableDivRef.current.contains(savedRange.commonAncestorContainer)
    ) {
      // Restore selection and insert at cursor position
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        selection.addRange(savedRange)
      }

      // Insert each video span at cursor position
      videoSpans.forEach((span, index) => {
        savedRange.insertNode(span)
        // Move cursor after inserted span
        savedRange.setStartAfter(span)
        savedRange.collapse(true)
        // Add space between videos
        if (index < videoSpans.length - 1) {
          const space = document.createTextNode(' ')
          savedRange.insertNode(space)
          savedRange.setStartAfter(space)
          savedRange.collapse(true)
        }
      })
    } else {
      // Fallback: append at end of content (when no cursor or savedRange is null)
      if (editableDivRef.current) {
        videoSpans.forEach((span, index) => {
          if (index > 0) {
            editableDivRef.current!.appendChild(document.createTextNode(' '))
          }
          editableDivRef.current!.appendChild(span)
        })
      }
    }

    // Update content
    const html = editableDivRef.current.innerHTML
    const formatted = convertHtmlToFormat(html)
    onChange(formatted)

    // Use setTimeout to ensure DOM is updated before attaching listeners
    setTimeout(() => {
      attachLinkListeners()
    }, 0)
    setVideoDrawerOpen(false)
    cleanDrawerState()
  }

  // Delete badge from editor
  const handleDeleteBadge = () => {
    if (!editableDivRef.current || !onChange || !selectedRangeElement.element)
      return

    const element = selectedRangeElement.element as HTMLElement

    // Remove element from DOM
    element.remove()

    // Update content
    const html = editableDivRef.current.innerHTML
    const formatted = convertHtmlToFormat(html)
    onChange(formatted)

    // Re-attach listeners after DOM update
    setTimeout(() => {
      attachLinkListeners()
    }, 0)

    // Clear selection state
    setSavedRange(null)
    setSelectedRangeElement({ element: null, position: null })
  }

  const convertFormatToHtml = useCallback(
    (text: string) => {
      const documentIds = documents.map(document => document._id)

      // const brPlaceholder = '___BR_PLACEHOLDER___'
      // const processedText = text.replace(/<br\s*\/?>/gi, brPlaceholder)

      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // escapedText = escapedText.replace(new RegExp(brPlaceholder, 'g'), '<br>')

      return escapedText
        .replace(linkRegex, (_match, linkText, documentId) => {
          const documentIdValid = documentId
            .split(';')
            .filter((id: string) => {
              const idOnly = id.replace('document_id://', '').split('?')[0]

              return documentIds.includes(idOnly)
            })
            .join(';')

          return documentIdValid.length
            ? `<span class="linkedText" data-document-id="document_id://${documentId}" data-markdown="[${linkText}](document_id://${documentId})">${getDocumentLinkIconHtml()}${linkText}</span>`
            : linkText
        })
        .replace(imageRegex, (_match, altText, imageIdsString) => {
          // Parse multiple IDs separated by semicolon (same pattern as Reference)
          const imageIds = imageIdsString
            .split(';')
            .map(id => id.replace('image://', '').trim())
            .filter(Boolean)

          // Skip if any ID is a blob URL (old data, should not happen with new uploads)
          if (imageIds.some(id => id.startsWith('blob:'))) {
            return _match // Return original markdown, don't convert to badge
          }

          // Convert image markdown to HTML badge
          const displayText = altText || 'Image'
          const idsString = imageIds.map(id => `image://${id}`).join(';')
          const markdown = `![${displayText}](${idsString})`
          return `<span class="embeddedImage" data-image-id="${imageIds.join(';')}" data-markdown="${markdown}">${getImageIconHtml()}${displayText}</span>`
        })
        .replace(videoRegex, (_match, title, videoIdsString) => {
          // Parse multiple IDs separated by semicolon (same pattern as Image)
          const videoIds = videoIdsString
            .split(';')
            .map(id => id.replace('video://', '').trim())
            .filter(Boolean)

          // Skip if any ID is a blob URL (old data, should not happen with new uploads)
          if (videoIds.some(id => id.startsWith('blob:'))) {
            return _match // Return original markdown, don't convert to badge
          }

          // Convert video markdown to HTML badge
          const displayText = title || 'Video'
          const idsString = videoIds.map(id => `video://${id}`).join(';')
          const markdown = `!video[${displayText}](${idsString})`
          return `<span class="embeddedVideo" data-video-id="${videoIds.join(';')}" data-markdown="${markdown}">${getVideoIconHtml()}${displayText}</span>`
        })
    },
    [documents],
  )

  useEffect(() => {
    if (
      isLoaded &&
      editableDivRef.current &&
      value !== convertHtmlToFormat(editableDivRef.current.innerHTML)
    ) {
      editableDivRef.current.innerHTML = convertFormatToHtml(value)
      attachLinkListeners()
    }
  }, [value, isLoaded, convertFormatToHtml])

  useEffect(() => {
    // Set up a MutationObserver to watch for changes to the DOM
    const observer = new MutationObserver(() => {
      attachLinkListeners()
    })

    if (editableDivRef.current) {
      observer.observe(editableDivRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })

      // Initial setup
      attachLinkListeners()
    }

    return () => {
      observer.disconnect()
      // Clean up existing click listeners
      const linkedText = document.querySelectorAll('.linkedText')
      linkedText.forEach(span => {
        span.removeEventListener('click', handleLinkedTextClick)
      })
    }
  }, [])

  const handleTooltipSelectionRangeLeave = () => {
    // Hide tooltip when mouse leaves it
    setTimeout(() => {
      setSelectedRangeElement({ element: null, position: null })
    }, 200)
  }

  const handleSelectionChange = useCallback(() => {
    // If any drawer is open, do not update selection to avoid clearing selectedRangeElement
    if (drawerOpen || imageDrawerOpen || videoDrawerOpen) return

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0)
      const text = range.toString().trim()

      if (text && editableDivRef.current?.contains(selection.anchorNode)) {
        const rect = range.getBoundingClientRect()
        const editableDivRect = editableDivRef.current.getBoundingClientRect()

        setSelectedRangeElement({
          element: range.startContainer.parentElement,
          position: {
            top: rect.top - editableDivRect.top + 16,
            left: rect.left - editableDivRect.left + 16,
          },
        })
      } else {
        setSelectedRangeElement({ element: null, position: null })
      }
    } else {
      setSelectedRangeElement({ element: null, position: null })
    }
  }, [drawerOpen, imageDrawerOpen, videoDrawerOpen])

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [handleSelectionChange])

  if (viewMode)
    return (
      <EditableDiv
        ref={editableDivRef}
        contentEditable
        onInput={handleInput}
        suppressContentEditableWarning
        className={className}
        style={style}
        data-placeholder={placeholder}
        onBlur={e => {
          if (['<p><br></p>', '<br>'].includes(e.currentTarget.innerHTML)) {
            e.currentTarget.innerHTML = ''
          }
        }}
        sx={{
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: '#aaa',
            pointerEvents: 'none',
            display: 'block',
          },
        }}
      />
    )

  return (
    <Box
      sx={{ position: 'relative', border: '1px solid #ddd', borderRadius: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 1,
          mb: 1,
          mx: '12px',
          borderBottom: '1px solid #BEC3D1',
          gap: 1,
        }}
      >
        <Button
          sx={{ color: '#676267' }}
          onClick={() => {
            // Clean state from other drawers first
            setDrawerOpen(false)
            setVideoDrawerOpen(false)
            setSelectedDocumentIds([])
            setVideoDrawerInitialData(undefined)
            setSelectedText('')
            setSelectedRangeElement({ element: null, position: null })

            // Save current cursor position before opening drawer
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              // Only save if cursor is within the editable div
              if (
                editableDivRef.current?.contains(range.commonAncestorContainer)
              ) {
                setSavedRange(range.cloneRange())
              } else {
                setSavedRange(null)
              }
            } else {
              setSavedRange(null)
            }
            setImageDrawerInitialData(undefined)
            setImageDrawerOpen(true)
          }}
          startIcon={<ImageIcon />}
          color="inherit"
        >
          {t('Add Image')}
        </Button>
        <Button
          sx={{ color: '#676267' }}
          onClick={() => {
            // Clean state from other drawers first
            setDrawerOpen(false)
            setImageDrawerOpen(false)
            setSelectedDocumentIds([])
            setImageDrawerInitialData(undefined)
            setSelectedText('')
            setSelectedRangeElement({ element: null, position: null })

            // Save current cursor position before opening drawer
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              // Only save if cursor is within the editable div
              if (
                editableDivRef.current?.contains(range.commonAncestorContainer)
              ) {
                setSavedRange(range.cloneRange())
              } else {
                setSavedRange(null)
              }
            } else {
              setSavedRange(null)
            }
            setVideoDrawerInitialData(undefined)
            setVideoDrawerOpen(true)
          }}
          startIcon={<VideoLibraryIcon />}
          color="inherit"
        >
          {t('Link Video')}
        </Button>
        <Button
          sx={{ color: '#676267' }}
          onClick={handleIconClick}
          startIcon={<DatasetLinkedIcon />}
          color="inherit"
        >
          {t('Link Reference')}
        </Button>
      </Box>

      <Paper
        elevation={1}
        sx={{
          position: 'relative',
          minHeight: '150px',
          p: 2,
        }}
      >
        {loading ? (
          <Box>
            <CircularProgress size="20px" color="inherit" sx={{ mr: 2 }} />
            Generating...
          </Box>
        ) : (
          <EditableDiv
            ref={editableDivRef}
            onCopy={e => {
              const selection = window.getSelection()
              if (!selection || selection.rangeCount === 0) {
                return
              }

              const range = selection.getRangeAt(0)
              const clonedContents = range.cloneContents()

              // Check if selection contains any badges
              if (selectionContainsBadges(clonedContents)) {
                e.preventDefault()

                // Extract markdown from selection
                const markdownText =
                  extractMarkdownFromSelection(clonedContents)
                const htmlText = getHtmlFromClonedContents(clonedContents)

                // Set clipboard data with markdown format
                e.clipboardData.setData('text/plain', markdownText)
                e.clipboardData.setData('text/html', htmlText)
              }
            }}
            onCut={e => {
              const selection = window.getSelection()
              if (!selection || selection.rangeCount === 0) {
                return
              }

              const range = selection.getRangeAt(0)
              const clonedContents = range.cloneContents()

              // Check if selection contains any badges
              if (selectionContainsBadges(clonedContents)) {
                e.preventDefault()

                // Extract markdown from selection
                const markdownText =
                  extractMarkdownFromSelection(clonedContents)
                const htmlText = getHtmlFromClonedContents(clonedContents)

                // Set clipboard data with markdown format
                e.clipboardData.setData('text/plain', markdownText)
                e.clipboardData.setData('text/html', htmlText)

                // Delete the selected content (this is what makes it "cut" instead of "copy")
                range.deleteContents()

                // Update content
                if (editableDivRef.current) {
                  const html = editableDivRef.current.innerHTML
                  const formatted = convertHtmlToFormat(html)
                  if (onChange) {
                    onChange(formatted)
                  }
                }

                // Re-attach listeners after DOM update
                setTimeout(() => {
                  attachLinkListeners()
                }, 0)
              }
            }}
            onPaste={e => {
              e.preventDefault()
              const pastedText = e.clipboardData.getData('text/plain')

              if (!editableDivRef.current) return

              // Convert markdown format to HTML badges
              let convertedHtml = convertFormatToHtml(pastedText)

              // Convert newlines to <br> tags for proper rendering
              // Replace \n with <br>, but preserve existing HTML structure
              convertedHtml = convertedHtml.replace(/\n/g, '<br>')

              // Create a temporary div to parse the HTML
              const tempDiv = document.createElement('div')
              tempDiv.innerHTML = convertedHtml

              // Get current selection
              const selection = window.getSelection()
              if (!selection || selection.rangeCount === 0) {
                // If no selection, append to end
                editableDivRef.current.innerHTML += tempDiv.innerHTML
                // Trigger input to update value
                handleInput()
                return
              }

              const range = selection.getRangeAt(0)
              range.deleteContents()

              // Insert all nodes from the converted HTML
              const fragment = document.createDocumentFragment()
              while (tempDiv.firstChild) {
                fragment.appendChild(tempDiv.firstChild)
              }

              range.insertNode(fragment)

              // Move cursor to end of inserted content
              range.collapse(false)
              selection.removeAllRanges()
              selection.addRange(range)

              // Trigger input to update value
              handleInput()
            }}
            contentEditable
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            suppressContentEditableWarning
            className={className}
            style={style}
            data-placeholder={placeholder}
            onBlur={e => {
              if (['<p><br></p>', '<br>'].includes(e.currentTarget.innerHTML)) {
                e.currentTarget.innerHTML = ''
              }
            }}
            sx={{
              '&:empty:before': {
                content: 'attr(data-placeholder)',
                color: '#aaa',
                pointerEvents: 'none',
                display: 'block',
              },
            }}
          />
        )}
      </Paper>
      {selectedRangeElement.element && selectedRangeElement.position && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: `${selectedRangeElement.position.top}px`,
            left: `${selectedRangeElement.position.left}px`,
            zIndex: 1000,
            display: 'flex',
            padding: '2px',
            backgroundColor: '#f5f5f5',
            transform: `translate(90%, 25%)`,
          }}
          onMouseLeave={handleTooltipSelectionRangeLeave}
        >
          <Tooltip title="Link Image">
            <IconButton size="small" onClick={() => handleImageIconClick()}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Link Video">
            <IconButton size="small" onClick={() => handleVideoIconClick()}>
              <VideoLibraryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Link Reference">
            <IconButton size="small" onClick={() => handleIconClick()}>
              <DatasetLinkedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      <DocumentDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onDocumentSelect={handleDocumentSelect}
        onDelete={handleDeleteBadge}
        selectedDocumentIds={selectedDocumentIds}
        initialLabel={selectedText}
        yearOptions={yearOptions}
        moduleOptions={moduleOptions}
        topicOptions={topicOptions}
        documents={documents}
      />

      <ImageDrawer
        open={imageDrawerOpen}
        onClose={() => {
          setImageDrawerOpen(false)
          cleanDrawerState()
        }}
        onSave={handleImageSave}
        onRemoveId={handleRemoveImageId}
        onDelete={handleDeleteBadge}
        initialData={imageDrawerInitialData}
      />

      <VideoDrawer
        open={videoDrawerOpen}
        onClose={() => {
          setVideoDrawerOpen(false)
          cleanDrawerState()
        }}
        onSave={handleVideoSave}
        onDelete={handleDeleteBadge}
        initialData={videoDrawerInitialData}
      />
    </Box>
  )
}
